import React, { useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LOGO_URL, LOGO_ALT } from '../../config/constants';
import SearchIcon from '../common/SearchIcon';

const navItems = [
  { label: 'Home', path: '/' },
  {
    label: 'Le Battaglie',
    path: '/temi',
    megaMenu: [
      {
        title: 'Visione Politica', links: [
          { label: 'Tematiche', path: '/temi' },
          { label: 'Campagne Nazionali', path: '/campagne' },
        ]
      }
    ],
    featuredCard: {
      title: "Salario Minimo Subito",
      label: "Attivati",
      link: "/campagna?id=salario-minimo",
      image: "https://images.unsplash.com/photo-1540317580384-e5d43616b9aa?auto=format&fit=crop&q=80&w=400"
    }
  },
  {
    label: "L'Organizzazione",
    path: '/chi-siamo',
    megaMenu: [
      {
        title: 'Chi Siamo', links: [
          { label: 'Perché Giovani Democratici', path: '/perche-giovani-democratici' },
          { label: 'Chi Siamo', path: '/chi-siamo' },
          { label: 'Organizzazione', path: '/organizzazione' },
          { label: 'Dipartimenti', path: '/dipartimenti' },
        ]
      },
      {
        title: 'Persone', links: [
          { label: 'Segreteria Nazionale', path: '/segreteria' },
          { label: 'Segretari Territoriali', path: '/segretari' },
          { label: 'Eletti', path: '/eletti' },
          { label: 'Rubrica Generale', path: '/persone' },
        ]
      },
      {
        title: 'Documenti', links: [
          { label: 'Tutti i Documenti', path: '/documenti' },
          { label: 'Trasparenza', path: '/trasparenza' },
        ]
      },
    ],
    featuredCard: {
      title: "Campagna Tesseramento",
      label: "Partecipa",
      link: "/partecipa",
      image: "https://images.unsplash.com/photo-1529156069898-49953eb1b565?auto=format&fit=crop&q=80&w=400"
    }
  },
  {
    label: 'Il Territorio',
    path: '/circoli',
    megaMenu: [
      {
        title: 'Territorio', links: [
          { label: 'Circoli', path: '/circoli' },
          { label: 'Federazioni Regionali', path: '/federazioni-regionali' },
          { label: 'Federazioni Provinciali', path: '/federazioni-provinciali' },
        ]
      },
      {
        title: 'Iniziative', links: [
          { label: 'Eventi', path: '/eventi' },
        ]
      },
    ],
    featuredCard: {
      title: "Trova il tuo Circolo",
      label: "Mappa",
      link: "/circoli",
      image: "https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=400"
    }
  },
  {
    label: 'News & Media',
    path: '/notizie',
    megaMenu: [
      {
        title: 'Media', links: [
          { label: 'Notizie', path: '/notizie' },
          { label: 'Video', path: '/video' },
          { label: 'Podcast', path: '/podcast' },
          { label: 'Area Stampa', path: '/stampa' },
        ]
      },
    ],
    featuredCard: {
      title: "Ultimo Episodio Podcast",
      label: "Ascolta Ora",
      link: "/podcast",
      image: "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?auto=format&fit=crop&q=80&w=400"
    }
  },
];

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
);

const Header = ({ transparent = false, onHamburgerClick, menuOpen = false, onSearchClick }) => {
  const location = useLocation();

  const handleScroll = useCallback(() => {
    const header = document.querySelector('.header');
    if (header) {
      header.classList.toggle('header--scrolled', window.scrollY > 10);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const isActive = (item) => {
    if (location.pathname === item.path) return true;
    if (item.megaMenu) {
      return item.megaMenu.some((g) => g.links.some((l) => l.path === location.pathname));
    }
    return false;
  };

  return (
    <header className={`header${transparent ? ' header--transparent' : ''}`} role="banner">
      <div className="container header__inner">
        <Link to="/" className="header__logo" aria-label="Giovani Democratici - Home">
          <img src={LOGO_URL} alt={LOGO_ALT} className="header__logo-img" style={{ height: 48, width: 'auto' }} />
        </Link>

        <nav className="nav" role="navigation" aria-label="Navigazione principale">
          {navItems.map((item) => (
            <div className="nav__item" key={item.path}>
              <Link to={item.path} className={`nav__link${isActive(item) ? ' nav__link--active' : ''}`}>
                {item.label}
                {item.megaMenu && <ChevronDown />}
              </Link>
              {item.megaMenu && (
                <div className="mega-menu">
                  <div className="container mega-menu__grid">
                    {item.megaMenu.map((group) => (
                      <div key={group.title}>
                        <div className="mega-menu__title">{group.title}</div>
                        {group.links.map((link) => (
                          <Link to={link.path} className="mega-menu__link" key={link.path}>{link.label}</Link>
                        ))}
                      </div>
                    ))}
                    {item.featuredCard && (
                      <div className="mega-menu__featured" style={{ gridColumn: '4 / 5' }}>
                        <Link to={item.featuredCard.link} className="card" style={{ display: 'block', textDecoration: 'none' }}>
                          <div className="card__image" style={{ aspectRatio: '16/9' }}>
                            <img src={item.featuredCard.image} alt={item.featuredCard.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                          <div className="card__body" style={{ padding: 'var(--space-4)' }}>
                            <div className="card__meta" style={{ marginBottom: 'var(--space-2)' }}>
                              <span style={{ color: 'var(--color-primary)' }}>{item.featuredCard.label}</span>
                            </div>
                            <h4 className="card__title" style={{ fontSize: 'var(--text-h6)', marginBottom: 0 }}>{item.featuredCard.title}</h4>
                          </div>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="header__actions">
          <button className="header__search-btn" aria-label="Cerca" onClick={onSearchClick}>
            <SearchIcon />
          </button>
          <Link to="/partecipa" className="btn btn--primary btn--sm hide-mobile">
            Partecipa
          </Link>
          <button
            className={`hamburger${menuOpen ? ' is-active' : ''}`}
            aria-label="Apri menu"
            aria-expanded={menuOpen}
            onClick={onHamburgerClick}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
